import { useState } from 'react';

/**
 * Grouped vertical bar chart, drawn as plain SVG - no chart library.
 *
 * Rules it follows on purpose:
 *  - ONE value axis. Never mix counts and amounts in the same chart.
 *  - Categorical colours come from a validated palette (see CHART_SERIES in
 *    charts/palette.js); they are assigned in fixed order, never cycled.
 *  - A legend is always drawn for two or more series, so identity is never
 *    carried by colour alone.
 *  - Grid and axes stay recessive; the data is the loudest thing on screen.
 */
const VB_W = 800;
const VB_H = 300;
const PAD = { top: 16, right: 12, bottom: 40, left: 62 };

const niceCeil = (value) => {
  if (value <= 0) return 1;
  const magnitude = 10 ** Math.floor(Math.log10(value));
  const scaled = value / magnitude;
  const step = scaled <= 1 ? 1 : scaled <= 2 ? 2 : scaled <= 2.5 ? 2.5 : scaled <= 5 ? 5 : 10;
  return step * magnitude;
};

/** Top-rounded bar: 4px radius on the data end, square on the baseline. */
const barPath = (x, y, width, height, radius = 4) => {
  const r = Math.min(radius, width / 2, Math.max(height, 0));
  if (height <= 0) return '';
  return `M${x},${y + height} L${x},${y + r} Q${x},${y} ${x + r},${y} L${x + width - r},${y} Q${x + width},${y} ${x + width},${y + r} L${x + width},${y + height} Z`;
};

const BarChart = ({
  data = [],
  series = [],
  formatValue = (v) => v,
  formatPeriod = (p) => p,
  emptyLabel = 'No data',
  height = 300,
}) => {
  const [hover, setHover] = useState(null);

  if (!data.length || !series.length) return <p className="chart-empty">{emptyLabel}</p>;

  const plotW = VB_W - PAD.left - PAD.right;
  const plotH = VB_H - PAD.top - PAD.bottom;

  const max = niceCeil(Math.max(1, ...data.flatMap((row) => series.map((s) => Number(row[s.key]) || 0))));
  const yOf = (value) => PAD.top + plotH - (value / max) * plotH;

  const groupW = plotW / data.length;
  // 2px of surface between neighbouring bars keeps them readable when short.
  const gap = 2;
  const innerW = Math.max(4, groupW * 0.7);
  const barW = Math.max(3, (innerW - gap * (series.length - 1)) / series.length);

  const ticks = 4;
  const gridValues = Array.from({ length: ticks + 1 }, (_, i) => (max / ticks) * i);

  // Only label a readable number of periods.
  const labelEvery = Math.ceil(data.length / 12);

  return (
    <div className="chart">
      {series.length > 1 ? (
        <div className="chart__legend">
          {series.map((s) => (
            <span key={s.key} className="chart__legend-item">
              <span className="chart__legend-dot" style={{ background: s.color }} aria-hidden="true" />
              {s.label}
            </span>
          ))}
        </div>
      ) : null}

      <div className="chart__canvas" style={{ '--chart-height': `${height}px` }}>
        <svg viewBox={`0 0 ${VB_W} ${VB_H}`} role="img" aria-label={series.map((s) => s.label).join(', ')}>
          {/* recessive grid */}
          {gridValues.map((value) => (
            <g key={value}>
              <line
                x1={PAD.left}
                x2={VB_W - PAD.right}
                y1={yOf(value)}
                y2={yOf(value)}
                className="chart__grid"
              />
              <text x={PAD.left - 8} y={yOf(value) + 4} className="chart__axis-label" textAnchor="end">
                {formatValue(value, true)}
              </text>
            </g>
          ))}

          {data.map((row, index) => {
            const groupX = PAD.left + index * groupW;
            const startX = groupX + (groupW - innerW) / 2;

            return (
              <g key={row.period}>
                {/* full-height hover target, easier to hit than the bar itself */}
                <rect
                  x={groupX}
                  y={PAD.top}
                  width={groupW}
                  height={plotH}
                  fill="transparent"
                  onMouseEnter={() => setHover({ row, x: groupX + groupW / 2 })}
                  onMouseLeave={() => setHover(null)}
                />
                {series.map((s, si) => {
                  const value = Number(row[s.key]) || 0;
                  const y = yOf(value);
                  const h = PAD.top + plotH - y;
                  return (
                    <path
                      key={s.key}
                      d={barPath(startX + si * (barW + gap), y, barW, h)}
                      fill={s.color}
                      className={hover && hover.row.period !== row.period ? 'chart__bar chart__bar--dim' : 'chart__bar'}
                    />
                  );
                })}

                {index % labelEvery === 0 ? (
                  <text
                    x={groupX + groupW / 2}
                    y={VB_H - PAD.bottom + 18}
                    className="chart__axis-label"
                    textAnchor="middle"
                  >
                    {formatPeriod(row.period)}
                  </text>
                ) : null}
              </g>
            );
          })}

          {/* baseline */}
          <line
            x1={PAD.left}
            x2={VB_W - PAD.right}
            y1={PAD.top + plotH}
            y2={PAD.top + plotH}
            className="chart__axis"
          />
        </svg>

        {hover ? (
          <div
            className="chart__tooltip"
            style={{
              insetInlineStart: `${(hover.x / VB_W) * 100}%`,
              transform: 'translateX(-50%)',
            }}
          >
            <p className="chart__tooltip-title">{formatPeriod(hover.row.period, true)}</p>
            {series.map((s) => (
              <p key={s.key} className="chart__tooltip-row">
                <span className="chart__legend-dot" style={{ background: s.color }} aria-hidden="true" />
                <span className="chart__tooltip-label">{s.label}</span>
                <strong>{formatValue(hover.row[s.key])}</strong>
              </p>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default BarChart;
